import { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Grid, Stack,
  Input, Select, Option, IconButton, CircularProgress,
} from '@mui/joy';
import { Add, Search, Visibility, Edit, Delete, ArrowUpward, ArrowDownward, Lock } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { projectService } from '@/services/projectService';
import { Project, ProjectStatus, PROJECT_STATUS_LABELS } from '@/types/project';
import ProjectStatusChip from '@/components/ProjectStatusChip';
import ProjectForm from '@/components/ProjectForm';
import toast from 'react-hot-toast';

const ALL_STATUSES: ProjectStatus[] = ['not_started', 'in_progress', 'completed', 'on_hold', 'cancelled'];

type SortField = 'created_at' | 'name' | 'start_date' | 'end_date';
type SortDir = 'asc' | 'desc';

export default function ProjectsPage() {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const canCreate = permissions.includes('projects.create');
  const canEdit = permissions.includes('projects.edit');
  const canDelete = permissions.includes('projects.delete');

  const load = async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (search) filters.search = search;
      if (statusFilter) filters.status = statusFilter;
      const data = await projectService.getProjects(filters);
      setProjects(data);
    } catch {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [search, statusFilter]);

  const handleCreate = async (data: any) => {
    await projectService.createProject(data);
    toast.success('Project created');
    load();
  };

  const handleUpdate = async (data: any) => {
    if (!editingProject) return;
    await projectService.updateProject(editingProject.id, data);
    toast.success('Project updated');
    setEditingProject(null);
    load();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this project and all its tasks?')) return;
    try {
      await projectService.deleteProject(id);
      toast.success('Project deleted');
      load();
    } catch {
      toast.error('Failed to delete project');
    }
  };

  const handleSortField = (field: SortField) => {
    if (field === sortField) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => {
      let aVal: string | null;
      let bVal: string | null;

      if (sortField === 'name') {
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
      } else {
        aVal = a[sortField];
        bVal = b[sortField];
      }

      // Nulls last regardless of direction
      if (!aVal && !bVal) return 0;
      if (!aVal) return 1;
      if (!bVal) return -1;

      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [projects, sortField, sortDir]);

  const formatDate = (d: string | null) => d ? new Date(d).toLocaleDateString() : '—';

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography level="h3">Projects</Typography>
        {canCreate && (
          <Button startDecorator={<Add />} onClick={() => setFormOpen(true)}>New Project</Button>
        )}
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
        <Input
          placeholder="Search projects..."
          startDecorator={<Search />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 200, maxWidth: 360 }}
        />
        <Select
          value={statusFilter}
          onChange={(_, v) => setStatusFilter(v ?? '')}
          placeholder="All Statuses"
          sx={{ minWidth: 160 }}
        >
          <Option value="">All Statuses</Option>
          {ALL_STATUSES.map(s => (
            <Option key={s} value={s}>{PROJECT_STATUS_LABELS[s]}</Option>
          ))}
        </Select>
        <Select
          value={sortField}
          onChange={(_, v) => v && handleSortField(v)}
          sx={{ minWidth: 160 }}
          startDecorator={sortDir === 'asc' ? <ArrowUpward sx={{ fontSize: 16 }} /> : <ArrowDownward sx={{ fontSize: 16 }} />}
        >
          <Option value="created_at">Date Created</Option>
          <Option value="name">Name</Option>
          <Option value="start_date">Start Date</Option>
          <Option value="end_date">End Date</Option>
        </Select>
        <IconButton
          variant="outlined"
          size="sm"
          onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
          title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
        >
          {sortDir === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
        </IconButton>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : projects.length === 0 ? (
        <Typography level="body-md" sx={{ textAlign: 'center', py: 6 }}>No projects found.</Typography>
      ) : (
        <Grid container spacing={2}>
          {sortedProjects.map(p => (
            <Grid key={p.id} xs={12} sm={6} md={4}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography level="title-md" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/projects/${p.id}`)}>
                          {p.name}
                        </Typography>
                        {p.is_private && <Lock sx={{ fontSize: 16, color: 'text.tertiary' }} />}
                      </Stack>
                      <ProjectStatusChip status={p.status} />
                    </Stack>
                    {p.description && (
                      <Typography level="body-sm" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {p.description}
                      </Typography>
                    )}
                    <Typography level="body-xs" color="neutral">
                      {formatDate(p.start_date)} — {formatDate(p.end_date)}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="sm" variant="plain" onClick={() => navigate(`/projects/${p.id}`)}>
                        <Visibility />
                      </IconButton>
                      {canEdit && (
                        <IconButton size="sm" variant="plain" onClick={() => { setEditingProject(p); setFormOpen(true); }}>
                          <Edit />
                        </IconButton>
                      )}
                      {canDelete && (
                        <IconButton size="sm" variant="plain" color="danger" onClick={() => handleDelete(p.id)}>
                          <Delete />
                        </IconButton>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <ProjectForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditingProject(null); }}
        onSubmit={editingProject ? handleUpdate : handleCreate}
        project={editingProject}
      />
    </Box>
  );
}
